import { ArrowButton } from "@/components/mypage/arrowButton";
import { createDemoUser, isDemoMode } from "@/utils/demoUser";
import { ThumbaGiftPointIcon } from "@roman/shared/components/Icons";
import { SYSTEM_FEE_MULTIPLIER } from "@roman/shared/constants";
import { useAuthStore } from "@roman/shared/store/auth.store";
import { pointsToMiraiMile } from "@roman/shared/utils/pointsToMiraiMile";
import { yenToPoints } from "@roman/shared/utils/pointsToYen";
import type React from "react";
import { useMemo } from "react";
import type { CardQuantity, MethodContent } from "..";
import { PriceSelectionCard } from "./PriceSelectionCard";

interface PaymentFormProps {
  methodContent: MethodContent | null;
  totalConsumingPoints: number;
  onClick: () => void;
  cardQuantities: CardQuantity[];
  setCardQuantities: React.Dispatch<React.SetStateAction<CardQuantity[]>>;
  backButtonClick: () => void;
}

export const MAXIMUM_WITHDRAWAL_YEN = 10000 * SYSTEM_FEE_MULTIPLIER;
export const MAXIMUM_WITHDRAWAL_POINTS = yenToPoints(MAXIMUM_WITHDRAWAL_YEN);

export const PaymentForm: React.FC<PaymentFormProps> = ({
  methodContent,
  totalConsumingPoints,
  onClick,
  cardQuantities,
  setCardQuantities,
  backButtonClick,
}) => {
  const user = isDemoMode() ? createDemoUser() : useAuthStore((state) => state.user);
  if (!user) {
    throw new Error("User not found");
  }

  const handleQuantityChange = (yenOption: string, quantity: number) => {
    const newQuantities: CardQuantity[] =
      quantity === 0
        ? cardQuantities.filter((item) => item.yenOption !== yenOption)
        : [...cardQuantities.filter((item) => item.yenOption !== yenOption), { yenOption, quantity }];

    if (totalConsumingPoints >= Number(user.redeemablePoints)) {
      window.alert("所持マイルが不足しています");
      return;
    }

    setCardQuantities(newQuantities);
  };

  const balancePoints = useMemo(() => {
    return Math.min(Number(user?.redeemablePoints || "0"), MAXIMUM_WITHDRAWAL_POINTS) - totalConsumingPoints;
  }, [totalConsumingPoints, user?.redeemablePoints]);

  if (!methodContent) {
    return;
  }

  return (
    <div className="bg-white min-w-full min-h-screen">
      <form className="p-[18px] flex flex-col items-start gap-10">
        <div className="flex flex-col items-start gap-3 w-full">
          <div className="flex items-center">
            <div className="w-[30px]" onClick={backButtonClick}>
              <ArrowButton className="-rotate-90" />
            </div>
            <p className="text-xl font-bold -tracking-[.01em] text-black">{methodContent.heading}</p>
          </div>
          <div className="flex flex-col items-start gap-1">
            <p className="text-[22px] text-primary font-bold -tracking-[.01em]">{methodContent.exchangeRange}</p>
            <p className="text-[10px] font-normal -tracking-[.01em] text-gray">{methodContent.description}</p>
          </div>
        </div>

        <div className="flex flex-col items-start gap-3 w-full">
          <div className="flex items-center">
            <p className="text-xl font-bold -tracking-[.01em] text-black">交換額を選定</p>
          </div>

          <div className="grid grid-cols-2 gap-3 w-full">
            {methodContent.availableYenOptions.map((yen) => (
              <PriceSelectionCard
                key={`price-${yen}`}
                yen={yen}
                balancePoints={balancePoints}
                onYenChange={(yen, quantity) => handleQuantityChange(yen.toString(), quantity)}
                initialQuantity={cardQuantities.find((item) => item.yenOption === yen)?.quantity || 0}
              />
            ))}
          </div>
        </div>
        <div className="flex flex-col items-start gap-3 w-full">
          <div className="flex items-center">
            <p className="text-xl font-bold -tracking-[.01em] text-black">メールアドレス</p>
          </div>
          <div className="flex flex-col items-start gap-3 w-full">
            <p className="text-base w-full font-normal h-[19px] pb-[2rem] border-b border-disabled border-solid outline-none">
              {user.email}
            </p>
          </div>
          <div>
            <p className="text-primary text-xs font-medium">上記のメールアドレスにギフトが送信されます</p>
          </div>
        </div>
        <div className="flex flex-col items-start gap-3 w-full">
          <div className="flex items-center">
            <p className="text-xl font-bold -tracking-[.01em] text-black">商品詳細</p>
          </div>
          <div>
            <p className="text-xs font-gray">
              詳細は
              <a href="#todo" className="text-primary">
                利用規約
              </a>
              を確認
            </p>
          </div>
        </div>

        <div className="flex flex-col items-start gap-3 w-full">
          <div className="flex items-center">
            <p className="text-xl font-bold -tracking-[.01em] text-black">合計消費マイル</p>
          </div>
          <div className="p-1 bg-[#EFF9FF] flex items-center justify-end w-full">
            <div className="flex items-center gap-2.5">
              <div className="h-6 w-6 flex items-center justify-center">
                <ThumbaGiftPointIcon />
              </div>
              <p className="text-primary text-xl font-bold">
                {totalConsumingPoints > 0 ? `${pointsToMiraiMile(totalConsumingPoints)}` : "-"}
                マイル
              </p>
            </div>
          </div>
          <div className="flex items-center justify-end w-full gap-3">
            <p className="text-gray text-xs font-medium">交換可能な所持マイル</p>
            <div className="flex items-center gap-1">
              <div className="h-4 w-4 flex items-center justify-center">
                <ThumbaGiftPointIcon />
              </div>
              <p className="text-gray text-xs font-medium">
                {totalConsumingPoints > 0
                  ? `${pointsToMiraiMile(Math.min(Number(user?.redeemablePoints || "0"), MAXIMUM_WITHDRAWAL_POINTS) - totalConsumingPoints)}`
                  : "-"}
              </p>
            </div>
          </div>
        </div>

        <button
          className={`flex items-center justify-center text-white py-2.5 px-4 text-lg font-bold w-full rounded-md ${
            totalConsumingPoints === 0 ? "bg-muted" : "bg-primary"
          }`}
          type="button"
          onClick={onClick}
          disabled={totalConsumingPoints === 0}
        >
          交換する
        </button>
      </form>
    </div>
  );
};
